import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DashboardDataTableColumnKind,
  type DashboardDataTableColumnDefinition,
} from '@shigo-ui/dashboard-ui';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shigo-ui/ui';

import { DashboardSelectionDialog } from '../../../../components/DashboardSelectionDialog';
import { formatUsd } from '../../../../lib/format';
import { dashboardT } from '../../../../lib/messages';
import { manageDialogFetchSize } from '../../../../lib/clientSelectionList';
import type { TargetingClient } from '../../api';
import type { TargetingFormValue, TargetingPlacement } from '../../model';
import { InclusionToggle } from './InclusionToggle';
import { ManagedSelectionSection } from './ManagedSelectionSection';
import { ManagedSelectionTable } from './ManagedSelectionTable';
import { managedSelectionTableClassNames } from './ManagedSelectionTable.styles';

const lookupFilter = { searchTerm: '', page: 0, size: manageDialogFetchSize };
const placementColumns: DashboardDataTableColumnDefinition<TargetingPlacement>[] = [
  {
    id: 'deliveryType',
    header: dashboardT('placements.deliveryType'),
    kind: DashboardDataTableColumnKind.Text,
    value: (placement) => placement.deliveryType ?? '—',
  },
  {
    id: 'bidFloor',
    header: dashboardT('placements.floor'),
    kind: DashboardDataTableColumnKind.Text,
    value: (placement) => formatUsd(placement.bidFloor, { fallback: '—' }),
  },
  {
    id: 'cpm',
    header: dashboardT('placements.cpm'),
    kind: DashboardDataTableColumnKind.Text,
    value: (placement) => formatUsd(placement.cpm, { fallback: '—' }),
  },
];

export function PlacementsTargetingSection({
  client,
  onChange,
  value,
}: {
  client: TargetingClient;
  onChange: (value: TargetingFormValue) => void;
  value: TargetingFormValue;
}) {
  const [open, setOpen] = useState(false);
  const selected = value.placementsIncluded ? value.includedPlacements : value.excludedPlacements;
  const query = useQuery({
    queryKey: [...(client.queryScope ?? []), 'targeting', 'placements', lookupFilter],
    queryFn: () => client.listPlacements(lookupFilter),
    enabled: open,
  });

  return (
    <>
      <ManagedSelectionSection
        title={dashboardT('targeting.placements')}
        selectedItems={selected}
        manageLabel={dashboardT('targeting.managePlacements')}
        emptyText={dashboardT('targeting.noPlacementsSelected')}
        onManage={() => {
          setOpen(true);
        }}
        onClear={() => {
          onChange({ ...value, includedPlacements: [], excludedPlacements: [] });
        }}
        action={
          <InclusionToggle
            included={value.placementsIncluded}
            onIncludedChange={(included) => {
              onChange({
                ...value,
                placementsIncluded: included,
                includedPlacements: included ? selected : [],
                excludedPlacements: included ? [] : selected,
              });
            }}
          />
        }
      >
        <ManagedSelectionTable
          aria-label={dashboardT('targeting.placements')}
          className="table-fixed"
        >
          <TableHeader className={managedSelectionTableClassNames.header}>
            <TableRow className={managedSelectionTableClassNames.row}>
              <TableHead className={managedSelectionTableClassNames.head}>
                {dashboardT('madBuy.common.name')}
              </TableHead>
              <TableHead className={`${managedSelectionTableClassNames.head} w-40`}>
                {dashboardT('placements.deliveryType')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {selected.map((placement) => (
              <TableRow key={placement.id} className={managedSelectionTableClassNames.row}>
                <TableCell
                  className={`${managedSelectionTableClassNames.cell} max-w-0 truncate font-medium`}
                  title={placement.name}
                >
                  {placement.name}
                </TableCell>
                <TableCell
                  className={`${managedSelectionTableClassNames.cell} text-muted-foreground`}
                >
                  {placement.deliveryType ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ManagedSelectionTable>
      </ManagedSelectionSection>
      <DashboardSelectionDialog
        open={open}
        title={dashboardT('targeting.managePlacements')}
        items={query.data?.content ?? []}
        selected={selected}
        loading={query.isLoading}
        error={query.isError}
        getId={(placement) => placement.id}
        getName={(placement) => placement.name}
        columns={placementColumns}
        onConfirm={(placements) => {
          onChange({
            ...value,
            includedPlacements: value.placementsIncluded ? placements : [],
            excludedPlacements: value.placementsIncluded ? [] : placements,
          });
        }}
        onOpenChange={setOpen}
      />
    </>
  );
}
